import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../Context/Createcontext";
import { Helmet } from "react-helmet";
import Popup from "../Components/common/Popup";
import ReactPaginate from "react-paginate";
import { api_url } from "../config/config";
import addToCartIcon from "../Icons/addToCartIcon.svg";
import { toast } from "react-toastify";
import wish from "../images/wish.svg";
import wishlist from "../images/wishlist_nofill.svg";
import { useWishlist } from "../Context/WishlistContext";
import { useApp } from "../Context/AppContext";

// ─── Sort Dropdown (identical to Concern) ────────────────────────────────────
const SORT_OPTIONS = [
  { value: "low-to-high", label: "Price: Low → High" },
  { value: "high-to-low", label: "Price: High → Low" },
  { value: "discount", label: "Best Discount" },
];

const SortDropdown = ({ sortOrder, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SORT_OPTIONS.find((o) => o.value === sortOrder) || SORT_OPTIONS[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 bg-white border border-[#e2eaea] hover:border-[#5fbdb3] text-[#374747] text-[13px] font-semibold px-4 h-10 rounded-xl transition-all duration-200 font-poppins whitespace-nowrap"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="8" y1="12" x2="16" y2="12" />
          <line x1="11" y1="18" x2="13" y2="18" />
        </svg>
        Sort: {current.label}
        <svg className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-[#e2eaea] rounded-2xl shadow-[0_8px_32px_rgba(22,44,42,0.16)] overflow-hidden min-w-[200px]">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full flex items-center justify-between px-4 py-3 text-[13px] font-semibold text-left transition-all font-poppins ${
                sortOrder === opt.value ? "bg-[#eaf4f3] text-[#1e4a46]" : "text-[#374747] hover:bg-[#f8fafa] hover:text-[#1e4a46]"
              }`}
            >
              {opt.label}
              {sortOrder === opt.value && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1e4a46" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Brandsall = ({ setCartCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    handleAddToCart,
    showLoginPopup,
    setShowLoginPopup,
    handleRemoveFromCart,
    setCartItems,
    cartItems,
  } = useCart();
  const {
    wishlist: wishlistItems,
    addToWishlist,
    removeFromWishlist,
    loading: wishlistLoading,
  } = useWishlist();
  const { contextCategories } = useApp();

  const [manufactid] = useState(location.state?.manufacturer_id);
  const [selectedBrandName] = useState(location.state?.manufacturer_name);

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [heroSearch, setHeroSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchCatTerm, setSearchCatTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("low-to-high");
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const productsPerPage = 20;

  const dropdownRef = useRef(null);

  // Close sidebar on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setSidebarOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Live search filter
  useEffect(() => {
    if (!heroSearch.trim()) {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(
        products.filter((p) =>
          p.name?.toLowerCase().includes(heroSearch.toLowerCase())
        )
      );
    }
    setCurrentPage(0);
  }, [heroSearch, products]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // ── Fetch all products for this brand ────────────────────────────────────
  const fetchProducts = async (sort = sortOrder, categoryIds = selectedCategories) => {
    if (!manufactid) return;
    setIsLoading(true);
    try {
      if (categoryIds.length === 0) {
        const res = await axios.get(
          `${api_url}/manufacturer-product/${manufactid}?page=1&limit=100&sort=${sort}`
        );
        const data = res.data.data || [];
        setProducts(data);
        setFilteredProducts(data);
      } else {
        const requests = categoryIds.map((categoryId) =>
          fetch(`${api_url}/category-manufacturer-products?page=1&limit=100&sort=${sort}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "x-auth": "false",
            },
            body: JSON.stringify({
              category_ids: [categoryId],
              manufacturer_ids: [manufactid],
            }),
          }).then((r) => r.json())
        );
        const results = await Promise.all(requests);
        const productMap = new Map();
        results.forEach((res) => {
          (res.data || []).forEach((p) => productMap.set(p.product_id, p));
        });
        const merged = Array.from(productMap.values());
        setProducts(merged);
        setFilteredProducts(merged);
      }
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setFilteredProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(sortOrder, selectedCategories);
  }, [manufactid, sortOrder, selectedCategories]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategories((prev) => {
      const updated = prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId];
      return updated;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClearCategories = () => {
    setSelectedCategories([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleProductClick = (product_id, product) =>
    navigate(`/products/${product.name?.toLowerCase().replace(/\s+/g, "-")}`, {
      state: { product_id },
    });

  const OutOfQuantityCheck = (variants) => variants.every((v) => v.quantity === 0);

  const getCartQuantity = (variantId) => {
    const item = cartItems.find((ci) => ci.variant_id === variantId);
    return item ? item.quantity : 0;
  };

  const handleIncrease = async (event, variant, item) => {
    event.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty + 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (!res.ok || data.message?.toLowerCase().includes("stock not available")) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id ? { ...ci, quantity: ci.quantity + 1 } : ci
      )
    );
  };

  const handleDecrease = async (event, variant) => {
    event.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    if (qty === 1) {
      await handleRemoveFromCart(variant.product_id, variant.variant_id);
      setCartItems((prev) => prev.filter((ci) => ci.variant_id !== variant.variant_id));
      return;
    }
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty - 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (!res.ok || data.message?.toLowerCase().includes("stock not available")) {
      toast.error("Something went wrong");
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id ? { ...ci, quantity: ci.quantity - 1 } : ci
      )
    );
  };

  const filteredCategories = contextCategories.filter((c) =>
    c.category_name?.toLowerCase().includes(searchCatTerm.toLowerCase())
  );

  const offset = currentPage * productsPerPage;
  const currentProducts = filteredProducts.slice(offset, offset + productsPerPage);
  const pageCount = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins">
      <Helmet>
        <title>Buy {selectedBrandName || "Brand"} Products | MediSYS Pharmacy</title>
        <meta name="description" content="MediSYS Pharmacy - Buy medicines online with fastest home delivery." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* ── Hero Banner ── */}
      <div className="bg-[#162c2a] relative overflow-hidden">
        {/* Plus pattern */}
        <div
          className="absolute inset-0 opacity-[0.045]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize: "40px 40px",
          }}
        />
        {/* Glow blobs */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#5fbdb3] opacity-[0.07] blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -right-16 w-72 h-72 rounded-full bg-[#ff601e] opacity-[0.06] blur-3xl pointer-events-none" />

        <div className="relative px-4 sm:px-6 lg:px-8 pt-6 pb-7">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 mb-4 flex-wrap">
            <Link to="/home" className="text-[11px] font-medium text-[#5fbdb3] hover:text-white transition-colors font-poppins">
              Home
            </Link>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4a6e6a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
            <Link to="/shopbrand" className="text-[11px] font-medium text-[#5fbdb3] hover:text-white transition-colors font-poppins">
              Shop by Brand
            </Link>
            {selectedBrandName && (
              <>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4a6e6a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
                <span className="text-[11px] font-medium text-white/60 font-poppins">{selectedBrandName}</span>
              </>
            )}
          </nav>

          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="w-11 h-11 rounded-xl bg-[#5fbdb3]/15 border border-[#5fbdb3]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>

            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-1.5 bg-[#ff601e]/15 border border-[#ff601e]/25 rounded-lg px-2.5 py-1 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ff601e]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#ff601e] font-poppins">Brand Products</span>
              </div>

              <h1 className="font-bold text-[22px] sm:text-[26px] lg:text-[30px] text-white leading-tight font-poppins">
                {selectedBrandName || "Brand"}
              </h1>
              <p className="text-[13px] text-[#5fbdb3] font-medium mt-1 font-poppins">
                {!isLoading
                  ? `${filteredProducts.length} product${filteredProducts.length !== 1 ? "s" : ""} found`
                  : "Loading products…"}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom accent bar */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#1e4a46] via-[#5fbdb3] to-[#ff601e]" />
      </div>

      {/* ── Search + Sort bar (identical to Concern) ── */}
      <div className="bg-white border-b border-[#e2eaea] px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search */}
          <div className="flex items-center gap-2.5 flex-1 bg-[#f8fafa] border border-[#e2eaea] focus-within:border-[#5fbdb3] focus-within:bg-white rounded-xl px-4 h-10 transition-all duration-200">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8faba9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={heroSearch}
              onChange={(e) => setHeroSearch(e.target.value)}
              placeholder="Search products…"
              className="flex-1 bg-transparent outline-none text-[13px] font-medium text-[#1e4a46] placeholder-[#8faba9] font-poppins"
            />
            {heroSearch && (
              <button onClick={() => setHeroSearch("")} className="text-[#8faba9] hover:text-[#1e4a46] transition-colors">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Mobile filter toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden flex items-center gap-2 text-[13px] font-semibold text-[#1e4a46] border border-[#d4ecea] bg-[#eaf4f3] px-4 h-10 rounded-xl"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="20" y2="12" />
                <line x1="12" y1="18" x2="20" y2="18" />
              </svg>
              Filter
              {selectedCategories.length > 0 && (
                <span className="bg-[#ff601e] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {selectedCategories.length}
                </span>
              )}
            </button>

            <SortDropdown
              sortOrder={sortOrder}
              onChange={(v) => { setSortOrder(v); setCurrentPage(0); }}
            />

            {!isLoading && (
              <div className="hidden sm:flex items-center gap-1.5 bg-[#eaf4f3] border border-[#d4ecea] rounded-xl px-3 h-10 flex-shrink-0">
                <span className="font-bold text-[13px] text-[#1e4a46]">{filteredProducts.length}</span>
                <span className="text-[12px] text-[#5fbdb3] font-medium">products</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* ── Sidebar: Categories (same structure as Concern's brands sidebar) ── */}
        <aside
          ref={dropdownRef}
          className={`${sidebarOpen ? "block" : "hidden"} lg:block lg:w-[260px] xl:w-[280px] flex-shrink-0 bg-white border-r border-[#e2eaea]`}
        >
          <div className="sticky top-0 p-5 space-y-6 max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="px-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[14px] text-[#ff601e]">Categories</h3>
                {selectedCategories.length > 0 && (
                  <button
                    onClick={handleClearCategories}
                    className="text-[11px] font-semibold text-[#ff601e] hover:underline"
                  >
                    Clear ({selectedCategories.length})
                  </button>
                )}
              </div>

              {/* Category search */}
              <div className="flex items-center gap-2 bg-[#f8fafa] border border-[#e2eaea] rounded-xl px-3 h-9 mb-3 focus-within:border-[#1e4a46] transition-all">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8faba9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search categories…"
                  value={searchCatTerm}
                  onChange={(e) => setSearchCatTerm(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-[12px] text-[#1a2625] placeholder-[#8faba9] font-poppins"
                />
              </div>

              {/* Category list */}
              <div className="flex flex-col gap-1 max-h-[260px] overflow-y-auto pr-1 scrollbar-hide">
                {filteredCategories.map((category) => {
                  const checked = selectedCategories.includes(category.category_id);
                  return (
                    <label
                      key={category.category_id}
                      className={`flex items-center gap-2.5 px-1 py-2.5 rounded-xl cursor-pointer transition-all ${
                        checked
                          ? "bg-[#eaf4f3] border border-[#d4ecea]"
                          : "hover:bg-[#f8fafa] border border-transparent"
                      }`}
                    >
                      <div
                        onClick={() => handleCategoryChange(category.category_id)}
                        className={`w-4 h-4 rounded-md border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all ${
                          checked ? "bg-[#1e4a46] border-[#1e4a46]" : "border-[#c5d5d4] bg-white"
                        }`}
                      >
                        {checked && (
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-[12px] font-poppins ${checked ? "font-semibold text-[#1e4a46]" : "text-[#374747]"}`}>
                        {category.category_name}
                      </span>
                    </label>
                  );
                })}
                {filteredCategories.length === 0 && (
                  <p className="text-[12px] text-[#8faba9] px-2 py-3 font-poppins">No categories found.</p>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Products Grid ── */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#e2eaea] overflow-hidden animate-pulse flex flex-col">
                  <div className="h-[160px] bg-[#e2eaea]" />
                  <div className="p-3 flex flex-col gap-2">
                    <div className="h-3 bg-[#e2eaea] rounded-full w-3/4" />
                    <div className="h-3 bg-[#e2eaea] rounded-full w-1/2" />
                    <div className="h-9 bg-[#e2eaea] rounded-xl mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : currentProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
              </div>
              <p className="font-semibold text-[15px] text-[#374747]">No products found</p>
              <p className="text-[13px] text-[#8faba9]">Try changing your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {currentProducts.map((product, index) => {
                const variants = product.variant || [];
                const prices = variants.map((v) => v.price).filter(Boolean);
                const discountedPrices = variants.map((v) => v.discounted_price).filter(Boolean);
                const minPrice = prices.length ? Math.min(...prices) : 0;
                const minDiscounted = discountedPrices.length ? Math.min(...discountedPrices) : minPrice;
                const discountPercent =
                  minPrice > minDiscounted
                    ? Math.round(((minPrice - minDiscounted) / minPrice) * 100)
                    : 0;
                const isWishlisted = wishlistItems.some(
                  (w) =>
                    w.product_id === product.product_id &&
                    w.variant_id === product.variant?.[0]?.variant_id
                );
                const isOutOfStock = OutOfQuantityCheck(variants);
                const cartQty = getCartQuantity(product.variant[0]?.variant_id);
                const isMultiVariant = Array.isArray(product.variant) && product.variant.length > 1;
                const isOrange = index % 2 === 1;
                const cardBg = isOrange ? "bg-gradient-to-b from-[#fff3ee] to-white" : "bg-gradient-to-b from-[#eaf4f3] to-white";
                const cardBorder = isOrange ? "border-[#ffd5c2] hover:border-[#ff601e]/60" : "border-[#e2eaea] hover:border-[#5fbdb3]";
                const accentBar = isOrange ? "from-[#ff601e] to-[#e55519]" : "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]";
                const imgBg = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
                const shadow = isOrange
                  ? "shadow-[0_2px_12px_rgba(255,96,30,0.09)] hover:shadow-[0_10px_28px_rgba(255,96,30,0.22)]"
                  : "shadow-[0_2px_12px_rgba(95,189,179,0.09)] hover:shadow-[0_10px_28px_rgba(95,189,179,0.22)]";
                const btnCls = isOrange
                  ? "bg-[#ff601e] hover:bg-[#e55519] shadow-[0_2px_8px_rgba(255,96,30,0.3)]"
                  : "bg-[#1e4a46] hover:bg-[#ff601e] shadow-[0_2px_8px_rgba(22,44,42,0.2)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)]";

                return (
                  <div
                    key={index}
                    className={`group border-[1.5px] ${cardBorder} ${cardBg} ${shadow} rounded-2xl overflow-hidden flex flex-col relative hover:-translate-y-2 transition-all duration-300 cursor-pointer`}
                    style={{ animation: `fadeInUp 0.35s ease ${(index % 8) * 40}ms both` }}
                  >
                    {/* Accent bar */}
                    <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accentBar} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`} />

                    {/* Discount badge */}
                    {discountPercent > 0 && (
                      <div className="absolute top-3 right-3 z-10 bg-[#ff601e] text-white text-[9px] sm:text-[10px] font-bold w-9 h-9 rounded-full flex flex-col items-center justify-center shadow-[0_2px_8px_rgba(255,96,30,0.45)] leading-tight text-center">
                        {discountPercent}%<br />OFF
                      </div>
                    )}

                    {/* Wishlist */}
                    <button
                      className="absolute top-3 left-3 z-10 w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-xl border border-[#e2eaea] flex items-center justify-center shadow-sm hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all duration-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!localStorage.getItem("authToken")) { setShowLoginPopup(true); return; }
                        isWishlisted
                          ? removeFromWishlist(product.product_id, product.variant?.[0]?.variant_id)
                          : addToWishlist(product, product.variant?.[0]);
                      }}
                      disabled={wishlistLoading}
                      onMouseEnter={() => setTooltipOpen(index)}
                      onMouseLeave={() => setTooltipOpen(null)}
                    >
                      <img src={isWishlisted ? wish : wishlist} alt="wishlist" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {tooltipOpen === index && (
                        <div className="absolute left-9 top-0 bg-[#162c2a] text-white text-[10px] font-medium px-2 py-1 rounded-lg whitespace-nowrap shadow z-30">
                          {isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                        </div>
                      )}
                    </button>

                    {/* Image */}
                    <div
                      className={`w-full h-[150px] sm:h-[170px] lg:h-[190px] ${imgBg} flex items-center justify-center overflow-hidden pt-6 pb-2 cursor-pointer`}
                      onClick={() => handleProductClick(product.product_id, product)}
                    >
                      <img
                        src={product?.product_images?.image_url?.primary_image || "/default.svg"}
                        alt={product.name || "Product"}
                        onError={(e) => { e.target.onerror = null; e.target.src = "/default.svg"; }}
                        className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>

                    <div className="w-full h-px bg-[#f1f5f5]" />

                    {/* Info */}
                    <div
                      className="px-3 sm:px-4 pt-3 pb-2 flex-1 flex flex-col gap-1.5 cursor-pointer"
                      onClick={() => handleProductClick(product.product_id, product)}
                    >
                      <h3 className="font-semibold text-[12px] sm:text-[13px] text-[#1e4a46] line-clamp-2 leading-snug group-hover:text-[#ff601e] transition-colors duration-200">
                        {product.name || "Unnamed Product"}
                      </h3>
                      <div className="flex items-baseline gap-2">
                        {discountPercent > 0 && (
                          <span className="text-[11px] text-[#8faba9] line-through">₹{minPrice}</span>
                        )}
                        <span className="font-bold text-[14px] sm:text-[15px] text-[#1e4a46]">₹{minDiscounted}</span>
                        {discountPercent > 0 && (
                          <span className={`text-[10px] font-semibold ${isOrange ? "text-[#ff601e]" : "text-[#5fbdb3]"}`}>
                            {discountPercent}% off
                          </span>
                        )}
                      </div>
                      {isOutOfStock && (
                        <span className="self-start text-[10px] font-semibold text-red-500 bg-red-50 border border-red-100 rounded-lg px-2 py-0.5">
                          Out of Stock
                        </span>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="px-3 sm:px-4 pb-4 pt-1">
                      {isMultiVariant ? (
                        <button
                          className={`w-full h-10 ${btnCls} text-white text-[11px] sm:text-[12px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200 disabled:bg-[#c5d5d4] disabled:cursor-not-allowed`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!localStorage.getItem("authToken")) { setShowLoginPopup(true); return; }
                            handleProductClick(product.product_id, product);
                          }}
                          disabled={isOutOfStock}
                        >
                          <img src={addToCartIcon} alt="Cart" className="w-3.5 h-3.5 object-contain" />
                          Choose Variant
                        </button>
                      ) : cartQty > 0 ? (
                        <div className="flex items-center justify-between bg-[#eaf4f3] border border-[#d4ecea] rounded-xl h-10 px-3">
                          <button
                            onClick={(e) => handleDecrease(e, product.variant[0], product)}
                            className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                          >−</button>
                          <span className="font-bold text-[14px] text-[#1e4a46] min-w-[24px] text-center">{cartQty}</span>
                          <button
                            onClick={(e) => handleIncrease(e, product.variant[0], product)}
                            className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                          >+</button>
                        </div>
                      ) : (
                        <button
                          className={`w-full h-10 ${btnCls} disabled:bg-[#c5d5d4] disabled:cursor-not-allowed text-white text-[11px] sm:text-[12px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!localStorage.getItem("authToken")) { setShowLoginPopup(true); return; }
                            await handleAddToCart(product, product.variant[0], 1);
                          }}
                          disabled={isOutOfStock}
                        >
                          <img src={addToCartIcon} alt="Cart" className="w-3.5 h-3.5 object-contain" />
                          {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Pagination (identical to Concern) ── */}
          {pageCount > 1 && (
            <div className="flex justify-center mt-8">
              <ReactPaginate
                previousLabel={
                  <span className="flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                    Prev
                  </span>
                }
                nextLabel={
                  <span className="flex items-center gap-1">
                    Next
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                }
                breakLabel="…"
                pageCount={pageCount}
                marginPagesDisplayed={1}
                pageRangeDisplayed={3}
                onPageChange={({ selected }) => {
                  setCurrentPage(selected);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                containerClassName="flex items-center gap-2 flex-wrap justify-center"
                pageClassName="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center text-[13px] font-semibold hover:bg-[#eaf4f3] hover:border-[#5fbdb3] cursor-pointer transition-all"
                activeClassName="!bg-[#1e4a46] !border-[#1e4a46] !text-white shadow-[0_2px_8px_rgba(22,44,42,0.25)]"
                previousClassName="flex items-center gap-1 h-9 px-3 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] text-[12px] font-semibold hover:bg-[#eaf4f3] hover:border-[#5fbdb3] cursor-pointer transition-all"
                nextClassName="flex items-center gap-1 h-9 px-3 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] text-[12px] font-semibold hover:bg-[#eaf4f3] hover:border-[#5fbdb3] cursor-pointer transition-all"
                disabledClassName="opacity-40 cursor-not-allowed hover:bg-white hover:border-[#e2eaea]"
                breakClassName="text-[#8faba9] text-[13px] px-1 flex items-center"
              />
            </div>
          )}
        </div>
      </div>

      {showLoginPopup && <Popup onClose={() => setShowLoginPopup(false)} />}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Brandsall;